import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid3X3,
  List,
  Plus,
  Home,
  ChevronRight,
  Search,
  X,
} from "lucide-react";
import {
  getSourceIcon,
  getDisplayName,
  DocumentStoreType,
  DOCUMENT_STORE_TABS,
} from "../../utils/documentHelpers";
import Can from "../auth/Can";

interface DocumentsHeaderProps {
  viewMode: "list" | "grid";
  setViewMode: (mode: "list" | "grid") => void;
  activeTab: DocumentStoreType;
  setActiveTab: (tab: DocumentStoreType) => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onCreateFolder: () => void;
  folderCounts?: Record<DocumentStoreType, number>;
}

const DocumentsHeader: React.FC<DocumentsHeaderProps> = ({
  viewMode,
  setViewMode,
  activeTab,
  setActiveTab,
  searchQuery,
  onSearchChange,
  onCreateFolder,
}) => {
  const navigate = useNavigate();

  return (
    <div className="sticky top-0 z-40 bg-white shadow-sm border-b border-gray-200">
      {/* Top section: Breadcrumb + Actions */}
      <div className="px-6 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm">
          <button
            onClick={() => navigate("/dashboard")}
            className="text-gray-500 hover:text-blue-600 transition-colors"
            aria-label="Go to dashboard"
          >
            <Home className="h-4 w-4" />
          </button>
          <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
          <h1 className="text-lg font-semibold text-gray-900">My Folders</h1>
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-2 self-end md:self-auto">
          {/* View Mode Toggle */}
          {activeTab === "All" && (
            <div className="flex items-center rounded-lg border border-gray-300 bg-gray-50 p-0.5 shadow-sm">
              <button
                onClick={() => setViewMode("list")}
                className={`p-1.5 rounded-md transition-all duration-200 ${
                  viewMode === "list"
                    ? "bg-blue-100 text-blue-600"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
                aria-label="List view"
                title="List view"
              >
                <List className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode("grid")}
                className={`p-1.5 rounded-md transition-all duration-200 ${
                  viewMode === "grid"
                    ? "bg-blue-100 text-blue-600"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
                aria-label="Grid view"
                title="Grid view"
              >
                <Grid3X3 className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Create Folder */}
          <Can permission="documents:create">
          <button
            onClick={onCreateFolder}
            className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 active:bg-blue-800 shadow-sm transition-all duration-200"
            title="Create new folder"
          >
            <Plus className="h-4 w-4" />
            <span>New Folder</span>
          </button>
          </Can>
        </div>
      </div>

      {/* Tabs + Search */}
      <div className="border-t border-gray-100 bg-gray-50/60">
        <div className="px-6 py-2 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          {/* Tabs */}
          <div className="flex overflow-x-auto no-scrollbar -mb-px">
            {DOCUMENT_STORE_TABS.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`group flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-all duration-200 ${
                  activeTab === tab
                    ? "border-blue-600 text-blue-600 bg-white shadow-sm"
                    : "border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-300"
                }`}
              >
                <div className="flex items-center gap-1.5">
                  {getSourceIcon(tab)}
                  <span className="hidden sm:inline">
                    {getDisplayName(tab)}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative w-full md:w-64">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search folders..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white py-1.5 pl-9 pr-8 text-sm text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-200"
            />
            {searchQuery && (
              <button
                onClick={() => onSearchChange("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-200 rounded-full transition-colors"
                title="Clear search"
              >
                <X className="h-3.5 w-3.5 text-gray-500" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentsHeader;
